import React, { useRef } from 'react';
import html2pdf from 'html2pdf.js';
import OrderPrintView, { OrderPrintRow } from './OrderPrintView';
import { Fact, ORDER_STATUS, Translate, fullDateTime } from '../../components/purchasing';

/**
 * The header facts of an order: who, where, source, then the dates in the order they happen.
 * deliveryNotes adds the supplier's delivery note numbers and the receiver's notes under Received.
 */
export const orderFacts = (order: any, t: Translate, options: { deliveryNotes?: boolean } = {}): Fact[] => {
  if (!order) return [];
  const statusId = Number(order.statusId);
  const client = order.client ?? {};
  const receipts: any[] = order.receipts ?? [];
  const last = receipts[receipts.length - 1];

  let approval: Pick<Fact, 'value' | 'pending' | 'lines'>;
  if (statusId === ORDER_STATUS.Rejected)
    approval = { value: t('Rejected'), lines: [order.approvedName, order.approvalDate ? fullDateTime(order.approvalDate) : null].filter(Boolean) };
  else if (order.approvalDate && statusId !== ORDER_STATUS.Draft) approval = { value: fullDateTime(order.approvalDate), lines: [order.approvedName].filter(Boolean) };
  else if (statusId === ORDER_STATUS.SentForApproval) approval = { pending: t('Waiting'), lines: [t('Any company admin can approve')] };
  else if (!client.requiresApproval) approval = { pending: t('Not needed for this supplier') };
  else if (statusId === ORDER_STATUS.Draft) approval = { pending: t('Not sent for approval yet') };
  else approval = { pending: '—' };

  let received: Pick<Fact, 'value' | 'pending' | 'lines'>;
  if (last) {
    const how = statusId === ORDER_STATUS.Received ? (receipts.length > 1 ? t('In {count} deliveries', { count: receipts.length }) : null) : t('Part received');
    const numbers: string[] = options.deliveryNotes ? receipts.map((receipt) => receipt.deliveryNoteNo).filter(Boolean) : [];
    const notes: string[] = options.deliveryNotes ? receipts.map((receipt) => receipt.note).filter(Boolean) : [];
    received = {
      value: fullDateTime(last.receivedAt),
      lines: [
        [how, last.receivedBy].filter(Boolean).join(' · '),
        numbers.length > 1 ? t('Delivery notes {numbers}', { numbers: numbers.join(', ') }) : numbers.length ? t('Delivery note {number}', { number: numbers[0] }) : '',
        ...notes.map((text) => t('Note: {text}', { text })),
      ].filter(Boolean),
    };
  } else if (statusId === ORDER_STATUS.Received) received = { value: t('Received') };
  else if (statusId === ORDER_STATUS.Cancelled) received = { pending: t('Nothing received') };
  else received = { pending: t('Not received yet') };

  return [
    {
      key: 'supplier',
      label: t('Supplier'),
      value: client.companyName || '—',
      lines: [client.clientNumber ? t('Supplier no. {number}', { number: client.clientNumber }) : null, client.emailAddress, client.phoneNumber].filter(Boolean) as string[],
      wide: true,
    },
    { key: 'site', label: t('Site'), value: order.location?.name, pending: '—' },
    { key: 'template', label: t('Template'), value: order.orderTemplate?.templateName, pending: t('No template') },
    { key: 'created', label: t('Created'), value: fullDateTime(order.createdAt), lines: [order.orderName].filter(Boolean), newRow: true, numeric: true },
    { key: 'approval', label: t('Approval'), numeric: true, ...approval },
    // A cancelled order that never went out will not be sent.
    { key: 'sent', label: t('Sent'), numeric: true, value: order.sentAt ? fullDateTime(order.sentAt) : undefined, pending: statusId === ORDER_STATUS.Cancelled ? '—' : t('Not sent yet'), lines: [order.sentByName].filter(Boolean) },
    { key: 'received', label: t('Received'), numeric: true, stackLines: true, ...received },
  ];
};

const docDate = (value?: string | null) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}.${date.getFullYear()}`;
};

/** The purchase-order document: rendered off screen, shown by Print and captured by Download PDF. */
export const useOrderPrint = (order: any, rows: OrderPrintRow[]) => {
  const preLanguage = localStorage.getItem('language');
  // Document language follows the site slug language (same as every other function); no manual chooser.
  const printLang: 'al' | 'en' = preLanguage === 'en' ? 'en' : 'al';
  const ref = useRef<HTMLDivElement>(null);

  const download = () => {
    const node = ref.current;
    if (!node) return;
    // html2pdf yields a blank canvas when .from() gets the position:absolute wrapper; capture the in-flow node inside it.
    const target = (node.firstElementChild as HTMLElement) ?? node;
    html2pdf()
      .set({
        margin: 8,
        filename: `purchase-order-${order?.internalNumber ?? ''}-${printLang}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: '#ffffff' },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      })
      .from(target)
      .save();
  };

  const node = (
    <>
      {/* During window.print() show only the document. */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          .order-print-content, .order-print-content * { visibility: visible; }
          .order-print-content { position: absolute !important; left: 0 !important; top: 0 !important; width: 100% !important; background: #fff; }
          .order-print-content > div { width: 100% !important; }
          .no-print { display: none !important; }
        }
      `}</style>
      <div className="order-print-content" ref={ref} style={{ position: 'absolute', left: '-9999px', top: 0 }}>
        <OrderPrintView
          lang={printLang}
          company={order?.company ?? null}
          supplier={order?.client ?? null}
          location={order?.location ?? null}
          orderNumber={order?.internalNumber}
          date={docDate(order?.createdAt) ?? '–'}
          statusId={order?.statusId}
          orderedBy={order?.orderName}
          approvedBy={order?.approvedName}
          approvalDate={docDate(order?.approvalDate)}
          vat={Number(order?.tax ?? 0)}
          rows={rows}
        />
      </div>
    </>
  );

  return { node, print: () => window.print(), download };
};
